import type { SerialRecard } from "@/lib/providers";

/**
 * СЛОВА ПЕРЕСОРТИЦЫ — отказы двери и отчёт о сделанном.
 *
 * Живут отдельно от окна по той же причине, что правила списания в
 * `lib/spisanie.ts`: это ПРАВИЛА, а не разметка, и проверяются они тестом, а
 * не глазами на живом складе. Окно остаётся печатью.
 */

/** Коды отказов `POST /api/serial/{serial}/card`, как их шлёт сервис. */
const REFUSALS: Record<string, string> = {
  serial_not_found: "Такой серии в CRM нет — проверьте номер.",
  card_not_found: "Такого размера у этого товара нет.",
  barcode_not_numeric: "Баркод — только цифры.",
  model_changed:
    "Этот размер принадлежит ДРУГОЙ модели. Такая замена меняет товар вещи и " +
    "стирает её описание — сделать её можно только в CRM.",
  size_unknown:
    "Размера этой карточки нет в справочнике размеров CRM — пересчитать размер " +
    "вещи нечем. Это чинится в справочнике, а не здесь.",
  crm_write_disabled: "Запись в CRM сейчас выключена.",
  crm_unavailable: "CRM недоступна — попробуйте позже.",
  unauthorized: "Сессия кончилась — войдите заново.",
  internal: "Не получилось: внутренняя ошибка. Скажите об этом разработчику.",
};

/**
 * Текст отказа для человека. Незнакомый код печатается как есть — молчать о
 * нём нельзя: «ничего не произошло» без причины отправляет человека жать
 * кнопку ещё раз.
 */
export function refusalMessage(code: string): string {
  return REFUSALS[code] ?? `Не получилось: ${code}`;
}

/** Итог удачной правки: что именно случилось с вещью. */
export type RecardOutcome = {
  /** Перечитать экран: карточка вещи стала другой. */
  reread: boolean;
  head: string;
  /** Поля серии, которые правка задела, — строками «поле: значение». */
  lines: string[];
};

/**
 * Имена полей CRM человеческими словами. Печатать `mSizeMin` кладовщику
 * бессмысленно, а молчать о правке — нечестно: она ушла в боевой учёт.
 */
const FIELDS: Record<string, string> = {
  mpProductCardID: "карточка маркетплейса",
  modelID: "модель",
  mSize: "размер вещи",
  mSizeMin: "наименьший размер",
  sSize: "размер буквой",
  sSizeWide: "полнота",
  mColor: "цвет модели",
  sColor: "цвет вещи",
  option: "опция маркетплейса",
  mSiteModelMediaID: "фотография модели",
  sSiteModelMediaID: "фотография вещи",
};

export function outcome(reply: SerialRecard, size: string): RecardOutcome {
  if (reply.already) {
    // Перечитывать нечего: записи не было вовсе. Сказать об этом обязательно —
    // иначе экран отчитается о работе, которой не было.
    return {
      reread: false,
      head: `Вещь и так числится размером ${size} — ничего не менялось.`,
      lines: [],
    };
  }

  const head = reply.model_changed
    ? `Готово: вещь переведена на ${size}, и товар сменился — описание вещи обнулено.`
    : `Готово: вещь числится размером ${size}.`;

  return {
    reread: true,
    head,
    lines: Object.entries(reply.changed).map(
      ([field, value]) =>
        `${FIELDS[field] ?? field}: ${value === null || value === "" ? "—" : value}`,
    ),
  };
}
